import { CheckedSmallIcon, CancelSmallIcon } from 'features/compositions/assets/icons/icons';
import { useCompositionsContext } from '../../context/CompositionsContext';
import styles from './EditableCompositionRow.module.css';
import CompositionRowField from './CompositionRowField';
import { useContext } from 'react';
import ThemeContext from 'config/context/themeContext';
import CompositionRowValue from './CompositionRowValue';

const EditableCompositionRow = ({ id, editMode }) => {
  const { dark } = useContext(ThemeContext);
  const { 
    toggleRowSelection, 
    isRowSelected, 
    selectedComposition,
    setEditedFieldValue,
    editedRowsData,
    setEditedRowsData
  } = useCompositionsContext();
  const isSelected = isRowSelected(id);

  const handleRowSelection = () => {
    toggleRowSelection(id);
  };

  // Find the row data using the id (Primary Key)
  const getRowData = () => {
    if (!selectedComposition?.rows || !Array.isArray(selectedComposition.rows)) return null;
    
    // Find row where the Primary Key matches the id
    const primaryKeyName = selectedComposition.structure.indexes[0]?.name;
    if (!primaryKeyName) return null;
    
    return selectedComposition.rows.find(row => row[primaryKeyName] === id);
  };

  const rowData = getRowData();

  // Get primary key field name
  const primaryKeyName = selectedComposition?.structure?.indexes?.[0]?.name;

  // Get the current value for a field (edited or original)
  const getValue = (fieldName) => {
    const edited = editedRowsData?.[id]?.[fieldName];
    if (edited !== undefined) return edited;
    return rowData?.[fieldName] ?? "";
  };

  // Check if field is editable (primary key is never editable)
  const isFieldEditable = (fieldName) => {
    return fieldName !== primaryKeyName;
  };

  // Cancel edits for this specific row
  const handleCancelRowEdit = () => {
    setEditedRowsData(prev => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const hasCancelButton = isSelected && editMode && editedRowsData[id];

  return (
    <div
      className={`${styles.editable_composition_row} ${isSelected ? styles.selected : ''} ${dark ? styles.dark : ''}`}
      onClick={() => {
        if (!editMode) handleRowSelection();
      }}
    >
      {hasCancelButton ? (
        <button
          className={styles.cancel_row_btn}
          onClick={(e) => {
            e.stopPropagation();
            handleCancelRowEdit();
          }}
        >
          <CancelSmallIcon />
        </button>
      ) : (
        <div className={styles.select_row_btn} >
          <CheckedSmallIcon />
        </div>
      )}
      {selectedComposition?.structure?.indexes?.map((i) => (
        <div key={i.meta_name} className={styles.field}>
          {isSelected && editMode && !(i?.ai || i?.is_auto_created || i?.is_auto_updated) ? (
            <CompositionRowField 
              type={i.type} 
              value={getValue(i.name)}
              onChange={(val) => setEditedFieldValue(id, i.name, val)}
              isEditable={isFieldEditable(i.name)}
              isArray={!!i?.is_array}
              fieldDefinition={i}
            />
          ) : (
            <CompositionRowValue type={i.type} value={rowData?.[i.name] ?? ""} />
          )}
        </div>
      ))}
      {selectedComposition?.structure?.fields?.map((f) => (
        <div key={f.meta_name} className={styles.field}>
          {isSelected && editMode && !(f?.ai || f?.is_auto_created || f?.is_auto_updated) ? (
            <CompositionRowField 
              type={f.type} 
              value={getValue(f.name)}
              onChange={(val) => setEditedFieldValue(id, f.name, val)}
              isEditable={isFieldEditable(f.name)}
              isObject={!!f.is_object}
              isArray={!!f?.is_array}
              fieldDefinition={f}
            />
          ) : (
            <CompositionRowValue type={f.type} value={rowData?.[f.name] ?? ""} />
          )}
        </div>
      ))}
    </div>
  )
}

export default EditableCompositionRow;